import { Dialog, DialogContent, Typography } from "@mui/material"
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles"
import CloseButton from "common/CloseButton"
import React from "react"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import shopTheme from "theme/shopTheme"
import { Font } from "../wizard/constants"
import { AuthState, setOpen } from "./authSlice"
import OTPForm from "./otp/OTPForm"
import PhoneForm from "./phone/PhoneForm"
import RegisterForm from "./register/RegisterForm"

const StyledDialogContent = styled(DialogContent)`
  position: relative;
  padding: ${(p) => p.theme.spacing(3)}px;
  width: 328px;

  :first-child {
    padding-top: ${(p) => p.theme.spacing(3)}px;
  }

  padding: 20px;
`
const StyledHeading = styled(Typography).attrs({ variant: "h2" })`
  text-align: center;
  margin-top: 0;
  margin-bottom: ${(p) => p.theme.spacing(3.5)}px;

  font-family: ${Font.OENaPro};
  font-size: 24px;
  font-weight: 400;
  line-height: 34px;
  letter-spacing: 0.05em;
`

const StyledOTPMainText = styled.p`
  font-family: ${Font.OENaPro};
  font-style: normal;
  font-weight: 300;
  font-size: 18px;
  line-height: 150%;
  text-align: center;
  margin-bottom: 20px;
`

const StyledPhoneText = styled.p`
  font-family: ${Font.OENaPro};
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: center;
`

const StyledOTPSubText = styled.p`
  font-family: ${Font.OENaPro};
  font-style: normal;
  font-weight: 300;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
`

const StyledDialog = styled(Dialog)`
  ${(p) => p.theme.breakpoints.down("md")} {
    .MuiDialog-paperWidthXs {
      max-height: 100%;
    }
  }

  ${(p) => p.theme.breakpoints.down("sm")} {
    .MuiDialog-paperWidthXs {
      max-height: 100%;
      max-width: 328px;
    }

    .MuiDialog-scrollPaper {
      align-items: flex-start;
    }
  }
`

type StepMap<T> = { [step in AuthState["step"]]: T }

const stepTitleMap = {
  Phone: "התחברות",
  OTP: "התחברות",
  Register: "הרשמה",
}

const formMap: StepMap<React.ComponentType> = {
  Phone: PhoneForm,
  OTP: OTPForm,
  Register: RegisterForm,
}

const AuthDialog = () => {
  const { open, step, temporaryPhone } = useTypedSelector((state) => state.auth)

  const dispatch = useAppDispatch()

  const Content = formMap[step]

  const stepContentMap = {
    Phone: (
      <StyledPhoneText>
        כדי לצפות בהטבות ולהמשיך לרכישה אנחנו צריכים לזהות אותך באמצעות התחברות
      </StyledPhoneText>
    ),
    OTP: (
      <>
        <StyledOTPMainText>
          קוד הזדהות נשלח לנייד {temporaryPhone.slice(-4)}•••••••
        </StyledOTPMainText>
        <StyledOTPSubText>הזינו כאן את הקוד שקיבלתם לנייד</StyledOTPSubText>
      </>
    ),
    Register: (
      <StyledPhoneText>
        נראה שזו פעם ראשונה שלך אצלינו, טוב שבאת! צריכים כמה פרטים להרשמה.
      </StyledPhoneText>
    ),
  }

  return (
    <MuiThemeProvider theme={shopTheme}>
      <StyledDialog
        open={open}
        onClose={() => dispatch(setOpen(false))}
        aria-label="התחברות"
        maxWidth="xs"
        disableRestoreFocus
        classes={{ paper: "authDialog" }}
        style={{ padding: "20px" }}
      >
        <StyledDialogContent>
          <CloseButton onClick={() => dispatch(setOpen(false))} />
          <StyledHeading>{stepTitleMap[step]}</StyledHeading>
          {stepContentMap[step]}
          <Content />
        </StyledDialogContent>
      </StyledDialog>
    </MuiThemeProvider>
  )
}

export default AuthDialog
